import React, { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Code2, Copy, ExternalLink, Check, Globe } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMBED_URL = `${BACKEND}/api/embed/bpk`;

const IFRAME_CODE = `<!-- Widget Struktur BPK GPDP Klasis Mimika -->
<iframe
  src="${EMBED_URL}"
  width="100%"
  height="900"
  frameborder="0"
  scrolling="auto"
  style="border:0; max-width: 900px; margin: 0 auto; display: block;"
  loading="lazy"
  title="Struktur BPK GPDP Klasis Mimika"
></iframe>`;

const JS_CODE = `<!-- Widget SIMPELKAMI GPDP (auto-resize) -->
<div id="simpel-bpk-widget"></div>
<script>
  (function() {
    var el = document.getElementById('simpel-bpk-widget');
    var iframe = document.createElement('iframe');
    iframe.src = '${EMBED_URL}';
    iframe.style.cssText = 'width:100%;border:0;min-height:900px;max-width:900px;display:block;margin:0 auto;';
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', 'Struktur BPK GPDP Klasis Mimika');
    el.appendChild(iframe);
  })();
</script>`;

const WORDPRESS_CODE = `[iframe src="${EMBED_URL}" width="100%" height="900"]`;

function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Kode berhasil disalin ke clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Gagal menyalin. Silakan copy manual."); }
  };
  return (
    <div className="relative">
      <pre className="text-xs bg-primary text-primary-foreground rounded-md p-4 overflow-x-auto font-mono leading-relaxed" data-testid={testid}>
        {code}
      </pre>
      <Button size="sm" variant="secondary" className="absolute top-2 right-2 h-7 rounded-full" onClick={copy} data-testid={`${testid}-copy`}>
        {copied ? <><Check className="h-3 w-3 mr-1" /> Tersalin</> : <><Copy className="h-3 w-3 mr-1" /> Salin</>}
      </Button>
    </div>
  );
}

export default function WidgetSync() {
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1">Sinkron ke Website Klasis</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Sematkan (embed) struktur BPK Klasis Mimika secara langsung ke website resmi klasis atau website lain.
          Data selalu sinkron otomatis — perubahan pada SIMPEL akan tampil real-time di website Anda.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="bg-primary text-primary-foreground border-primary">
          <CardContent className="p-5">
            <Globe className="h-6 w-6 mb-3 opacity-80" />
            <div className="text-[0.65rem] tracking-[0.18em] uppercase opacity-80">URL Widget</div>
            <div className="font-mono text-sm mt-2 break-all">{EMBED_URL}</div>
            <a href={EMBED_URL} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs underline opacity-90 hover:opacity-100" data-testid="link-preview-widget">
              Buka pratinjau <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Sinkron Otomatis</div>
            <div className="font-heading font-bold text-lg mt-2">Real-time</div>
            <div className="text-xs text-muted-foreground mt-1">Setiap pergantian pengurus di SIMPEL langsung muncul di widget tanpa deploy ulang website.</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Bebas Login</div>
            <div className="font-heading font-bold text-lg mt-2">Publik</div>
            <div className="text-xs text-muted-foreground mt-1">Widget hanya menampilkan data publik: nama, jabatan, dan periode SK. Data pribadi tetap aman.</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="iframe" className="mb-8">
        <TabsList data-testid="widget-tabs">
          <TabsTrigger value="iframe" data-testid="tab-iframe"><Code2 className="h-3.5 w-3.5 mr-1.5" /> iframe (HTML)</TabsTrigger>
          <TabsTrigger value="js" data-testid="tab-js">JavaScript</TabsTrigger>
          <TabsTrigger value="wp" data-testid="tab-wp">WordPress</TabsTrigger>
        </TabsList>
        <TabsContent value="iframe" className="mt-4 space-y-3">
          <div className="text-sm text-muted-foreground">Salin kode di bawah ini, lalu tempel pada halaman HTML website klasis (misalnya di halaman <em>Tentang Kami → Kepengurusan</em>).</div>
          <CodeBlock code={IFRAME_CODE} testid="code-iframe" />
        </TabsContent>
        <TabsContent value="js" className="mt-4 space-y-3">
          <div className="text-sm text-muted-foreground">Gunakan jika Anda ingin widget dimuat secara dinamis via JavaScript.</div>
          <CodeBlock code={JS_CODE} testid="code-js" />
        </TabsContent>
        <TabsContent value="wp" className="mt-4 space-y-3">
          <div className="text-sm text-muted-foreground">Untuk website WordPress, gunakan shortcode plugin <strong>iframe</strong> (misalnya "Advanced iframe").</div>
          <CodeBlock code={WORDPRESS_CODE} testid="code-wp" />
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Pratinjau Widget</CardTitle></CardHeader>
        <CardContent>
          <div className="border border-border rounded-md overflow-hidden bg-muted/30">
            <iframe src={EMBED_URL} title="Pratinjau Widget BPK" className="w-full" style={{ height: 900, border: 0 }} data-testid="widget-preview" />
          </div>
        </CardContent>
      </Card>
    </AppShell>
  );
}
